import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { uid } from '../../../../lib/uid';
import type { EmploymentType } from '../personnelTypes';

/**
 * Test data for the previous-employer form's Autogenerate.
 *
 * **Nothing here is resolved against a catalog**, unlike `sampleEligibility`, which must pick
 * a real row from the loaded eligibility list or the server refuses the id. A previous
 * employer's line has no foreign keys at all, so every value is simply made up.
 *
 * The rules that still bite: a start date in the future, an end date before the start, and —
 * since the merge — NO end date at all are refused server-side, so the dates are always a
 * closed span in the past.
 */
export interface SamplePreviousEmployment {
  effectiveFrom: string;
  effectiveTo: string;
  positionTitle: string;
  agencyName: string;
  monthlySalary: string;
  salaryGrade: string;
  stepIncrement: string;
  appointmentStatus: EmploymentType;
  sector: 'government' | 'private';
  remarks: string;
}

export function samplePreviousEmployment(): SamplePreviousEmployment {
  const tag = uid().slice(0, 6).toUpperCase();

  /*
    Calendar dates built from parts, never `toISOString` — that converts through UTC and can
    hand the field the day before the one intended. The day is capped at 28 so a 29 February
    today cannot become a date that does not exist in the generated year.
  */
  const today = todayCalendarDate();
  const [year, month, day] = today.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  const safeDay = Math.min(day, 28);

  // Two to eight years back, running for two of them — ended before today, begun before it ended.
  const startYear = year - 4 - Math.floor(Math.random() * 5);
  const endYear = startYear + 2;

  /*
    Government half the time, because both answers are worth exercising: the column is the one
    anybody aggregates on, and a generator that only produced one would leave the other
    untested by every click of the button.
  */
  const government = Math.random() < 0.5;

  return {
    effectiveFrom: `${startYear}-${pad(month)}-${pad(safeDay)}`,
    effectiveTo: `${endYear}-${pad(month)}-${pad(safeDay)}`,

    // Names an employer, never a person, so nothing here lands in the identity store.
    positionTitle: 'Administrative Officer II',
    agencyName: government ? `Test Agency ${tag}` : `Test Company ${tag}`,

    // A figure as stated, not a rate read from the salary schedule.
    monthlySalary: String(20000 + Math.floor(Math.random() * 30000)),
    salaryGrade: government ? String(10 + Math.floor(Math.random() * 15)) : 'Band B',
    stepIncrement: String(1 + Math.floor(Math.random() * 8)),

    // Permanent — the form's first listed status and the commonest real answer.
    appointmentStatus: 1,
    sector: government ? 'government' : 'private',
    remarks: `Generated test record ${tag}.`,
  };
}
